"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useGame } from "@/app/providers/GameContextProvider";
import ButtonFFXIV from "@/app/components/ButtonFFXIV";
import { AnimatePresence, motion } from "framer-motion";
import PlayerResultsList from "./PlayerResultsList";
import ErrorBoundary from "./ErrorBoundary";
import dynamic from "next/dynamic";
import { useTranslation } from "@/hooks/useTranslation";
import { BiSolidHide } from "react-icons/bi";
import TooltipFFXIV from "@/app/components/TooltipFFXIV";
import { useGameQuality } from '@/app/providers/GameQualityProvider';
import OverlayButtons from "../OverlayButtons";
import { hazards } from "@/app/(front)/components/GamePlay/hazards";
import HazardIcon from "../HazardIcon";
import FightModePlayerResultsList from "./FightModePlayerResultsList";
import CoopModePlayerResultsList from "./CoopModePlayerResultsList";
import { getBackendUrl } from "@/lib/utils/imageUrls";
import { usePhotosphereQuality } from "@/app/providers/PhotosphereQualityProvider";
import { useIsMobile } from "@/hooks/useIsMobile";
import { AUDIO, useAudio } from "@/app/providers/AudioProvider";
const ResultRecapMap = dynamic(() => import("./ResultRecapMap"), { ssr: false });

interface GameResultProps {
    showResultOverlay: boolean;
    setShowResultOverlay: React.Dispatch<React.SetStateAction<boolean>>;
    onHideHazardsChange?: (hideHazards: boolean) => void;
}

const GameResult = ({ showResultOverlay, setShowResultOverlay, onHideHazardsChange }: GameResultProps) => {
    const { gameState, room } = useGame();
    const { t } = useTranslation();
    const { getBackdropBlurIntensity } = useGameQuality();
    const { getPhotosphereUrl, quality } = usePhotosphereQuality();
    const isMobile = useIsMobile();
    const [isNextRoundLoading, setIsNextRoundLoading] = useState(false);
    const [hideHazards, setHideHazards] = useState(false);
    const [hoveredMapPlayer, setHoveredMapPlayer] = useState<string | null>(null);
    const [lockedMapPlayer, setLockedMapPlayer] = useState<string | null>(null);
    const { play } = useAudio();

    // Preload next photosphere for faster round transitions
    useEffect(() => {
        const nextPhotosphereId = gameState?.nextPhotosphere?.id;
        if (nextPhotosphereId && nextPhotosphereId !== "") {
            const backendUrl = getBackendUrl();
            
            // Preload the appropriate photosphere image
            const preloadUrl = gameState?.hasFlashModeActive
                ? `${backendUrl}/images/photospheres/${nextPhotosphereId}/panorama_light.webp`
                : getPhotosphereUrl(nextPhotosphereId);
            
            console.log(`🔄 Preloading next photosphere: ${nextPhotosphereId}`);
            
            // Use Image to preload - IMPORTANT: Set crossOrigin for WebGL/Three.js compatibility
            const img = new Image();
            img.crossOrigin = "anonymous"; // Required for Three.js texture loading
            img.src = preloadUrl;
            img.onload = () => {
                console.log(`✅ Preloaded next photosphere: ${nextPhotosphereId}`);
            };
            img.onerror = () => {
                console.warn(`⚠️ Failed to preload next photosphere: ${nextPhotosphereId}`);
            };
        }
    }, [gameState?.nextPhotosphere?.id, gameState?.hasFlashModeActive, getPhotosphereUrl, quality]);

    // Notify parent component when hideHazards changes
    useEffect(() => {
        if (onHideHazardsChange) {
            onHideHazardsChange(hideHazards);
        }
    }, [hideHazards, onHideHazardsChange]);

    // Get the current round result
    const currentRound = gameState?.currentRound;
    const currentRoundResult = gameState?.roundHistory.find(
        result => result.roundNumber === currentRound
    );

    const [displayedRoundResult, setDisplayedRoundResult] = useState<typeof currentRoundResult | undefined>(currentRoundResult);

    // Reset locked player when round changes
    useEffect(() => {
        setLockedMapPlayer(null);
    }, [gameState?.currentRound]);

    // Update displayedRoundResult when currentRoundResult changes
    useEffect(() => {
        if (currentRoundResult) {
            setDisplayedRoundResult(currentRoundResult);
        }
    }, [currentRoundResult]);



    const isGameFinished = useMemo(() => {
        if (!gameState || !currentRound) return false;
        if (gameState.maxRounds !== -1) {
            return currentRound >= gameState.maxRounds;
        } else {
            if (gameState.isEffectivelyEndless) {
                // In Endless mode, game is finished if all players are eliminated or no one got perfect score
                return Array.from(gameState.players?.values() || []).every(p => p.isEliminated);
            } else if (gameState.isEffectivelyFight) {
                // In Fight mode, game is finished if only one team remains
                const activeTeams = new Set<string>();
                gameState.players?.forEach(player => {
                    if (!player.isEliminated) {
                        activeTeams.add(player.teamId);
                    }
                });
                return activeTeams.size <= 1;
            }
        }
        return false;
    }, [currentRound, gameState?.gameSettings.maxRounds, gameState?.isEffectivelyEndless, gameState?.isEffectivelyFight]);

    if (!gameState || !room || !currentRound) return null;

    // Get current player
    const currentPlayer = gameState.players?.get(room.sessionId);

    const handleNextRound = async () => {
        setIsNextRoundLoading(true);
        try {
            // Send message to server to start next round
            room.send("nextRound");
        } catch (error) {
            console.error("Error starting next round:", error);
        } finally {
            setIsNextRoundLoading(false);
        }
    };

    const handleToggleReady = async () => {
        try {
            room.send("toggleReady");
        } catch (error) {
            console.error("Error toggling ready state:", error);
        }
    };

    return (
        <>
            <ErrorBoundary>
                <OverlayButtons
                    hideHazards={hideHazards}
                    setHideHazards={setHideHazards}
                    showResultOverlay={showResultOverlay}
                    setShowResultOverlay={setShowResultOverlay}
                />

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className={`relative w-full h-full z-[20] ${showResultOverlay ? ` ${getBackdropBlurIntensity('sm')}` : 'pointer-events-none'}`}
                >
                    {/* Result Overlay */}
                    <AnimatePresence>
                        {displayedRoundResult && (
                            <motion.div
                                key={`result-overlay-${displayedRoundResult.roundNumber}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: showResultOverlay ? 1 : 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                                className={`relative w-full h-full flex items-center justify-center ${!showResultOverlay ? 'no-pointer-events' : ''}`}
                                onAnimationComplete={() => {
                                    if (!showResultOverlay && !currentRoundResult) {
                                        setDisplayedRoundResult(undefined);
                                    }
                                }}
                            >
                                <div className={`flex flex-col w-full items-center max-w-3xl 3xl:max-w-7xl gap-3 2xl:gap-4 ${isMobile ? 'h-full pt-16 pb-4' : 'h-full md:mt-0 md:justify-center'}`}>
                                    <div className="relative text-white text-shadow shadow-blue-600 italic font-bold flex-shrink-0 flex gap-2 items-center">
                                        <div className="uppercase text-2xl 2xl:text-5xl ">
                                            {t("play.roundResults", { roundNumber: displayedRoundResult.roundNumber })}
                                        </div>
                                        <div className="md:absolute md:top-1/2 md:-right-7 md:translate-x-full md:-translate-y-1/2 text-2xl flex items-center gap-4">
                                            <div className="h-full flex gap-1 md:gap-3 items-center justify-center">
                                                {gameState?.currentHazards.map((hazardName, index) => {
                                                    const hazard = hazards.find(h => h.name === hazardName.toLowerCase());
                                                    if (!hazard) return null;
                                                    return <HazardIcon key={`hazard-icon-${index}`} hazard={hazard} size={isMobile ? 20 : 40} />;
                                                })}
                                            </div>
                                            {gameState.isEffectivelyFight && gameState.currentDamageMultiplier && (
                                                <div className="hidden md:block text-sm 2xl:text-3xl">Damage: x{gameState.currentDamageMultiplier.toFixed(2)}</div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="w-fit relative flex-shrink-0">
                                        <div className="absolute -top-1 -right-1 flex gap-2 z-[10000]">
                                            {/* Hide Results Button */}
                                            <div
                                                onMouseEnter={() => {play(AUDIO.HOVER)}}
                                                onClick={() => {play(AUDIO.CLICK); setShowResultOverlay(false)}}
                                                className="h-[40px] w-[40px] cursor-pointer rounded-[100%] flex items-center justify-center shadow-[0_0_3px_1px_rgba(0,0,0,0.5),inset_0_0_3px_1px_rgba(0,0,0,0.5)] hover:bg-[#6F6F6F] bg-[#575757] border-2 border-x-[#c0a270] border-y-[#e0c290] group"
                                            >
                                                <BiSolidHide className="text-2xl text-[#bea881]" />
                                                <TooltipFFXIV>
                                                    <span className="text-white">{t("play.hideResultsAndSeePhotosphere")}</span>
                                                </TooltipFFXIV>
                                            </div>
                                        </div>
                                        <ResultRecapMap
                                            key={`result-map-${displayedRoundResult.roundNumber}-${displayedRoundResult.photosphere.id}`}
                                            roundResult={displayedRoundResult}
                                            hoveredMapPlayer={lockedMapPlayer || hoveredMapPlayer}
                                            isOverlayVisible={showResultOverlay}
                                            width={"1280px"}
                                            height={"640px"}
                                        />
                                    </div>
                                    <div className={`w-full 2xl:w-fit ${isMobile ? 'flex-1 min-h-0 overflow-y-auto' : ''}`}>
                                        {gameState.isEffectivelyFight ? (
                                            <FightModePlayerResultsList
                                                roundResult={displayedRoundResult}
                                                onHoverMapPlayer={setHoveredMapPlayer}
                                                onClickMapPlayer={setLockedMapPlayer}
                                                lockedMapPlayer={lockedMapPlayer}
                                            />
                                        ) : gameState.isEffectivelyCoop ? (
                                            <CoopModePlayerResultsList
                                                roundResult={displayedRoundResult}
                                                onHoverMapPlayer={setHoveredMapPlayer}
                                                onClickMapPlayer={setLockedMapPlayer}
                                                lockedMapPlayer={lockedMapPlayer}
                                            />
                                        ) : (
                                            <PlayerResultsList
                                                roundResult={displayedRoundResult}
                                                onHoverMapPlayer={setHoveredMapPlayer}
                                                onClickMapPlayer={setLockedMapPlayer}
                                                lockedMapPlayer={lockedMapPlayer}
                                            />
                                        )}
                                    </div>

                                    <div className="flex-shrink-0">
                                        {room.sessionId === gameState.gameMasterId ? (
                                            <ButtonFFXIV
                                                text={isGameFinished ? t("play.viewFinalResults") : t("play.nextRound")}
                                                onClick={handleNextRound}
                                                disabled={isNextRoundLoading}
                                                goldenBorder={true}
                                                fitContent={true}
                                                className="text-sm !py-0 2xl:!py-1 !px-6 2xl:!px-10 2xl:text-base"
                                            />
                                        ) : (
                                            <ButtonFFXIV
                                                text={currentPlayer?.isReady ? t("play.notReady") : t("play.ready")}
                                                onClick={handleToggleReady}
                                                fitContent={true}
                                                className="text-sm !py-0 2xl:!py-1 !px-6 2xl:!px-10 2xl:text-base"
                                            />
                                        )}
                                        {isNextRoundLoading && (
                                            <p className="text-center text-gray-400 text-sm mt-2">
                                                {t("play.loading")}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </ErrorBoundary>
        </>
    );
};

export default GameResult;